/*
 * Free gift at a cart threshold (Theme settings → Cart progress bar → Free gift).
 *
 * The gift's price comes off through a Shopify automatic discount, "Amount off
 * order" for the gift's price on carts of the threshold or more, and that only
 * works once the gift is in the cart; nothing adds it. This keeps the cart in
 * step:
 *
 *   cart (without the gift) >= threshold  → add 1 gift line
 *   cart (without the gift) <  threshold  → remove the gift line
 *
 * The gift line carries the private property `_free_gift` (hidden at checkout),
 * so it stays separate from a jar of the same honey the shopper bought
 * themselves, and the drawer / cart page show it as a gift with no quantity
 * controls. The amount is every other line at full price, before discounts.
 * Shopify tests the order discount's minimum after the 5% / 10% product
 * discounts but with the gift line in, and the gift (₹349, ₹314 after 10%)
 * adds more than 10% of the threshold takes away, so the gift is in the cart
 * exactly when the discount applies. Measuring after the product discounts
 * instead would hold the gift back from a ₹1,999 cart that earned 10%.
 *
 * (Not Buy X get Y: while the % tiers are product discounts, Shopify won't let
 * the items that earn a BXGY gift also take the 10%.)
 *
 * Runs after every cart change the theme publishes (add to cart, quantity,
 * remove) and once on load, then redraws the drawer and the cart page.
 */
(function () {
  const cfg = window.vaFreeGift;
  if (!cfg || !cfg.variantId) return;

  const GIFT_KEY = '_free_gift';
  let running = false;
  let again = false;

  const isGift = (item) => item.properties && item.properties[GIFT_KEY];

  async function getCart() {
    const res = await fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  // Redraw the cart drawer (whole panel, so the progress bar updates too), the
  // cart icon count and, on /cart, the cart section — without opening anything.
  async function redraw() {
    const drawer = document.querySelector('cart-drawer');
    const pageSection = document.querySelector('cart-items')?.closest('.shopify-section');
    const ids = ['cart-icon-bubble'];
    if (drawer) ids.push('cart-drawer');
    const pageId = pageSection && pageSection.id.replace('shopify-section-', '');
    if (pageId) ids.push(pageId);

    const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    const sections = await res.json();
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

    if (drawer && sections['cart-drawer']) {
      const fresh = parse(sections['cart-drawer']).querySelector('.drawer__inner');
      const current = drawer.querySelector('.drawer__inner');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const fresh = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (fresh) bubble.innerHTML = fresh.innerHTML;
    }
    if (pageId && sections[pageId]) {
      const fresh = parse(sections[pageId]).querySelector('.shopify-section');
      if (fresh) pageSection.innerHTML = fresh.innerHTML;
    }
  }

  async function sync() {
    if (running) {
      again = true;
      return;
    }
    running = true;
    try {
      let changed = false;
      for (let pass = 0; pass < 3; pass++) {
        const cart = await getCart();
        const gifts = cart.items.filter(isGift);
        const fullPrice = cart.items
          .filter((i) => !isGift(i))
          .reduce((sum, i) => sum + i.original_line_price, 0);
        const qualifies = fullPrice >= cfg.threshold;

        if (qualifies && gifts.length === 0) {
          await post(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            items: [{ id: cfg.variantId, quantity: 1, properties: { [GIFT_KEY]: cfg.label } }],
          });
        } else if (!qualifies && gifts.length) {
          await post(`${window.routes?.cart_change_url || '/cart/change'}.js`, { id: gifts[0].key, quantity: 0 });
        } else if (gifts.length > 1 || (gifts[0] && gifts[0].quantity !== 1)) {
          // Exactly one gift, quantity one, whatever happened to the line.
          await post(`${window.routes?.cart_change_url || '/cart/change'}.js`, { id: gifts[0].key, quantity: 1 });
          for (const extra of gifts.slice(1)) {
            await post(`${window.routes?.cart_change_url || '/cart/change'}.js`, { id: extra.key, quantity: 0 });
          }
        } else {
          break;
        }
        changed = true;
      }
      if (changed) await redraw();
    } catch (e) {
      console.error('[free-gift]', e);
    } finally {
      running = false;
      if (again) {
        again = false;
        sync();
      }
    }
  }

  function start() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => sync());
    }
    sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
